import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Download } from 'lucide-react';
import API_BASE from '@/lib/apiBase';

export interface ComplianceDetailedFormat {
  formatKey: string;
  formatName: string;
  orderIndex: number;
}

export interface ComplianceDetailedCampaign {
  campaignId: number;
  campaignName: string;
  campaignType?: string;
  formats: ComplianceDetailedFormat[];
}

export interface ComplianceDetailedRow {
  storeId: number;
  storeName: string;
  installerName?: string;
  cells: Record<string, Record<string, { expectedQty: number; actualQty: number; unconfirmedQty?: number }>>;
  discrepancies?: Array<{ campaignName: string; formatName: string; reason: string }>;
}

export interface ComplianceDetailedResponse {
  retailerId: number;
  cycleId: number;
  campaigns: ComplianceDetailedCampaign[];
  rows: ComplianceDetailedRow[];
  lastOperatorPosting?: {
    postedAt: string;
    storeId: number;
    storeName: string;
    operatorId?: number | null;
    operatorName?: string | null;
  } | null;
}

export interface ComplianceOverviewFormat {
  formatKey: string;
  formatName: string;
  orderIndex: number;
}

export interface ComplianceOverviewRow {
  campaignId: number;
  campaignName: string;
  cells: Record<string, { expectedQty: number; actualQty: number }>;
  shortfalls: Record<string, Array<{ storeName: string; expectedQty: number; actualQty: number }>>;
  totalStoreCount: number;
  storesWithSomeInstalled: number;
  storesWithAllInstalled: number;
}

export interface ComplianceOverviewData {
  formats: ComplianceOverviewFormat[];
  rows: ComplianceOverviewRow[];
  uniqueStoreCount: number;
}

function getActualCellClasses(expectedQty: number, actualQty: number): string {
  if (expectedQty === 0) return 'bg-slate-50 text-slate-700';
  if (actualQty === expectedQty) return 'bg-emerald-50 text-emerald-800';
  if (actualQty > expectedQty) return 'bg-emerald-50 text-emerald-800';
  if (actualQty === 0 && expectedQty > 0) return 'bg-rose-50 text-rose-800';
  return 'bg-amber-50 text-amber-800';
}

export function buildComplianceOverviewData(data?: ComplianceDetailedResponse | null): ComplianceOverviewData {
  if (!data) {
    return { formats: [], rows: [], uniqueStoreCount: 0 };
  }

  const formatsByKey = new Map<string, ComplianceOverviewFormat>();
  for (const campaign of data.campaigns) {
    for (const format of campaign.formats) {
      if (!formatsByKey.has(format.formatKey)) {
        formatsByKey.set(format.formatKey, {
          formatKey: format.formatKey,
          formatName: format.formatName,
          orderIndex: formatsByKey.size,
        });
      }
    }
  }

  const formats = Array.from(formatsByKey.values()).sort((a, b) =>
    a.formatName.localeCompare(b.formatName) || a.formatKey.localeCompare(b.formatKey)
  ).map((format, index) => ({ ...format, orderIndex: index }));

  const rows = data.campaigns.map((campaign) => {
    const campaignId = String(campaign.campaignId);
    const cells: Record<string, { expectedQty: number; actualQty: number }> = {};
    const shortfalls: ComplianceOverviewRow['shortfalls'] = {};
    for (const format of formats) {
      cells[format.formatKey] = { expectedQty: 0, actualQty: 0 };
      shortfalls[format.formatKey] = [];
    }

    let totalStoreCount = 0;
    let storesWithSomeInstalled = 0;
    let storesWithAllInstalled = 0;

    for (const storeRow of data.rows) {
      const storeCells = storeRow.cells?.[campaignId] ?? {};
      let expectedTotal = 0;
      let actualTotal = 0;
      let hasIncompleteExpectedFormat = false;

      for (const format of formats) {
        const cell = storeCells[format.formatKey] ?? { expectedQty: 0, actualQty: 0 };
        cells[format.formatKey].expectedQty += Number(cell.expectedQty || 0);
        cells[format.formatKey].actualQty += Number(cell.actualQty || 0);

        expectedTotal += Number(cell.expectedQty || 0);
        actualTotal += Number(cell.actualQty || 0);
        if (cell.expectedQty > 0 && cell.actualQty < cell.expectedQty) {
          hasIncompleteExpectedFormat = true;
          shortfalls[format.formatKey].push({
            storeName: storeRow.storeName,
            expectedQty: Number(cell.expectedQty || 0),
            actualQty: Number(cell.actualQty || 0),
          });
        }
      }

      if (expectedTotal > 0) {
        totalStoreCount += 1;
        if (actualTotal > 0) storesWithSomeInstalled += 1;
        if (!hasIncompleteExpectedFormat) storesWithAllInstalled += 1;
      }
    }

    return {
      campaignId: campaign.campaignId,
      campaignName: campaign.campaignName,
      cells,
      shortfalls,
      totalStoreCount,
      storesWithSomeInstalled,
      storesWithAllInstalled,
    };
  });

  return {
    formats,
    rows,
    uniqueStoreCount: data.rows.length,
  };
}

interface Props {
  data?: ComplianceDetailedResponse | null;
  isLoading: boolean;
  error?: Error | null;
}

export function ComplianceDetailedGrid({ data, isLoading, error }: Props) {
  const [isExporting, setIsExporting] = useState(false);
  const lastPostingText = data?.lastOperatorPosting
    ? (() => {
        const postedAt = new Date(data.lastOperatorPosting.postedAt);
        const dateText = Number.isNaN(postedAt.getTime()) ? String(data.lastOperatorPosting.postedAt) : postedAt.toLocaleString();
        return `Last operator posting: ${dateText} · ${data.lastOperatorPosting.storeName} · ${data.lastOperatorPosting.operatorName || 'Unknown operator'}`;
      })()
    : null;

  const handleExportExcel = async () => {
    if (!data || !data.campaigns.length) return;
    setIsExporting(true);
    try {
      const { fetchWithSupabaseTokenRaw } = await import('@/lib/http-supa');
      const params = new URLSearchParams({
        retailerId: String(data.retailerId),
        cycleId: String(data.cycleId),
      });
      if (data.campaigns.length === 1) {
        params.set('campaignId', String(data.campaigns[0].campaignId));
      }
      const buffer = await fetchWithSupabaseTokenRaw(`${API_BASE}/api/compliance/detailed-export?${params.toString()}`);
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      a.href = url;
      a.download = `compliance-detailed-cycle-${data.cycleId}-${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (exportError) {
      console.error('Compliance export failed', exportError);
      const msg = exportError instanceof Error ? exportError.message : String(exportError);
      window.alert(`Failed to export Excel file:\n${msg}`);
    } finally {
      setIsExporting(false);
    }
  };

  const hasExportableData = !!data && data.campaigns.length > 0 && data.rows.length > 0;

  const header = (
    <CardHeader className="pb-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <CardTitle>Compliance Detailed</CardTitle>
          {lastPostingText ? (
            <div className="text-xs text-muted-foreground">{lastPostingText}</div>
          ) : null}
        </div>
        <Button size="sm" variant="outline" onClick={handleExportExcel} disabled={isExporting || !hasExportableData}>
          <Download className="h-4 w-4 mr-2" />
          {isExporting ? 'Exporting...' : 'Export Excel'}
        </Button>
      </div>
    </CardHeader>
  );

  if (isLoading) {
    return (
      <Card>
        {header}
        <CardContent>
          <div className="text-sm text-muted-foreground">Loading compliance data...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        {header}
        <CardContent>
          <div className="text-sm text-red-600">{error.message || 'Failed to load compliance data'}</div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.campaigns.length === 0) {
    return (
      <Card>
        {header}
        <CardContent>
          <div className="text-sm text-muted-foreground">No compliance rows found for the selected filters.</div>
        </CardContent>
      </Card>
    );
  }

  // Compute totals across all rows
  const totals: Record<string, Record<string, { expectedQty: number; actualQty: number }>> = {};
  for (const row of data.rows) {
    for (const campaign of data.campaigns) {
      const cKey = String(campaign.campaignId);
      if (!totals[cKey]) totals[cKey] = {};
      for (const format of campaign.formats) {
        const cell = row.cells?.[cKey]?.[format.formatKey] ?? { expectedQty: 0, actualQty: 0 };
        const t = totals[cKey][format.formatKey] ?? { expectedQty: 0, actualQty: 0 };
        t.expectedQty += cell.expectedQty;
        t.actualQty += cell.actualQty;
        totals[cKey][format.formatKey] = t;
      }
    }
  }

  return (
    <Card>
      {header}
      <CardContent>
        <div className="overflow-auto border rounded-md">
          <table className="w-full min-w-max border-collapse text-sm">
            <thead>
              <tr className="bg-slate-100">
                <th rowSpan={3} className="sticky left-0 z-20 min-w-[240px] border-b border-r px-3 py-2 text-left font-semibold bg-slate-100">
                  Store
                </th>
                {data.campaigns.map((campaign) => (
                  <th
                    key={campaign.campaignId}
                    colSpan={Math.max(1, campaign.formats.length * 2)}
                    className="border-b border-r px-2 py-2 text-center font-semibold"
                  >
                    {campaign.campaignName}
                  </th>
                ))}
                <th rowSpan={3} className="border-b border-l px-3 py-2 text-left font-semibold bg-slate-100 min-w-[220px]">
                  Discrepancy
                </th>
              </tr>
              <tr className="bg-slate-50">
                {data.campaigns.map((campaign) =>
                  campaign.formats.map((format) => (
                    <th key={`${campaign.campaignId}-${format.formatKey}`} colSpan={2} className="border-b border-r px-2 py-2 text-center font-medium">
                      {format.formatName}
                    </th>
                  ))
                )}
              </tr>
              <tr className="bg-white">
                {data.campaigns.map((campaign) =>
                  campaign.formats.flatMap((format) => ([
                    <th key={`${campaign.campaignId}-${format.formatKey}-expected`} className="border-b border-r px-2 py-1 text-center font-medium">
                      {format.formatName}
                    </th>,
                    <th key={`${campaign.campaignId}-${format.formatKey}-actual`} className="border-b border-r px-2 py-1 text-center font-medium">
                      Installed
                    </th>,
                  ]))
                )}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.storeId} className="odd:bg-white even:bg-slate-50/40">
                  <td className="sticky left-0 z-10 border-r border-b bg-inherit px-3 py-2 align-top">
                    <div className="font-medium">{row.storeName}</div>
                    {row.installerName ? (
                      <div className="text-xs text-muted-foreground truncate max-w-[220px]">{row.installerName}</div>
                    ) : null}
                  </td>
                  {data.campaigns.map((campaign) =>
                    campaign.formats.flatMap((format) => {
                      const cell = row.cells?.[String(campaign.campaignId)]?.[format.formatKey] || { expectedQty: 0, actualQty: 0 };
                      const isOver = cell.actualQty > cell.expectedQty;
                      return [
                        <td
                          key={`${row.storeId}-${campaign.campaignId}-${format.formatKey}-expected`}
                          className="border-r border-b px-2 py-2 text-center tabular-nums"
                        >
                          {cell.expectedQty}
                        </td>,
                        <td
                          key={`${row.storeId}-${campaign.campaignId}-${format.formatKey}-actual`}
                          className={`border-r border-b px-2 py-2 text-center tabular-nums ${getActualCellClasses(cell.expectedQty, cell.actualQty)}`}
                        >
                          <div className="inline-flex items-center gap-1">
                            <span>{cell.actualQty}</span>
                            {isOver ? <Badge variant="secondary" className="h-4 px-1 text-[10px]">Over</Badge> : null}
                            {cell.unconfirmedQty ? (
                              <Badge
                                variant="outline"
                                className="h-4 border-amber-400 px-1 text-[10px] text-amber-800"
                                title={`${cell.unconfirmedQty} left with store manager, installation unconfirmed`}
                              >
                                Unconfirmed
                              </Badge>
                            ) : null}
                          </div>
                        </td>,
                      ];
                    })
                  )}
                  <td className="border-l border-b px-3 py-2 align-top text-xs">
                    {row.discrepancies && row.discrepancies.length > 0 ? (
                      <ul className="space-y-1">
                        {row.discrepancies.map((d, i) => (
                          <li key={i} className="text-rose-700">
                            <span className="font-medium">{d.campaignName}</span>
                            {' · '}
                            <span className="font-medium">{d.formatName}</span>
                            {': '}
                            {d.reason}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </td>
                </tr>
              ))}
              {/* Totals row */}
              <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300">
                <td className="sticky left-0 z-10 border-r border-b bg-slate-100 px-3 py-2">
                  Totals
                </td>
                {data.campaigns.map((campaign) =>
                  campaign.formats.flatMap((format) => {
                    const t = totals[String(campaign.campaignId)]?.[format.formatKey] ?? { expectedQty: 0, actualQty: 0 };
                    return [
                      <td key={`totals-${campaign.campaignId}-${format.formatKey}-expected`} className="border-r border-b px-2 py-2 text-center tabular-nums">
                        {t.expectedQty}
                      </td>,
                      <td key={`totals-${campaign.campaignId}-${format.formatKey}-actual`} className="border-r border-b px-2 py-2 text-center tabular-nums">
                        {t.actualQty}
                      </td>,
                    ];
                  })
                )}
                <td className="border-l border-b px-3 py-2" />
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export function ComplianceOverviewGrid({ data, isLoading, error }: Props) {
  const [isExporting, setIsExporting] = useState(false);
  const overview = buildComplianceOverviewData(data);

  const handleExportExcel = async () => {
    if (!data || !data.campaigns.length) return;
    setIsExporting(true);
    try {
      const ExcelJS = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Compliance Overview');
      const teal = 'FF2F9BAA';
      const tealDark = 'FF1F6F7A';
      const lightTeal = 'FFE5F3F8';
      const stripe = 'FFF3FAFC';
      const borderColor = 'FF94A3B8';
      const summaryColumnCount = 4;
      const totalColumns = 1 + overview.formats.length * 2 + summaryColumnCount;
      const headerRow = 1;
      const pairHeaderRow = 2;

      worksheet.views = [{ state: 'frozen', xSplit: 1, ySplit: 2 }];
      worksheet.getCell(headerRow, 1).value = 'Campaign';
      worksheet.mergeCells(headerRow, 1, pairHeaderRow, 1);

      let col = 2;
      for (const format of overview.formats) {
        worksheet.getCell(headerRow, col).value = format.formatName;
        worksheet.mergeCells(headerRow, col, headerRow, col + 1);
        worksheet.getCell(pairHeaderRow, col).value = 'Due';
        worksheet.getCell(pairHeaderRow, col + 1).value = 'Installed';
        col += 2;
      }

      const summaryHeaders = [
        'Total no. of stores in campaign',
        'Stores with some POS installed',
        'Stores with all POS installed',
        'Notes',
      ];
      for (const headerText of summaryHeaders) {
        worksheet.getCell(headerRow, col).value = headerText;
        worksheet.mergeCells(headerRow, col, pairHeaderRow, col);
        col += 1;
      }

      for (let c = 1; c <= totalColumns; c += 1) {
        const topCell = worksheet.getCell(headerRow, c);
        const subCell = worksheet.getCell(pairHeaderRow, c);
        topCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: teal } };
        topCell.font = { color: { argb: 'FFFFFFFF' }, bold: true, size: 10 };
        topCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        subCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: lightTeal } };
        subCell.font = { color: { argb: tealDark }, bold: true, size: 10 };
        subCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      }

      let rowIndex = 3;
      for (const row of overview.rows) {
        const isStriped = rowIndex % 2 === 0;
        worksheet.getCell(rowIndex, 1).value = row.campaignName;
        worksheet.getCell(rowIndex, 1).font = { bold: true };
        worksheet.getCell(rowIndex, 1).alignment = { horizontal: 'left', vertical: 'middle' };

        let valueCol = 2;
        for (const format of overview.formats) {
          const cell = row.cells[format.formatKey] ?? { expectedQty: 0, actualQty: 0 };
          const dueCell = worksheet.getCell(rowIndex, valueCol);
          const installedCell = worksheet.getCell(rowIndex, valueCol + 1);
          dueCell.value = cell.expectedQty;
          installedCell.value = cell.actualQty;
          dueCell.alignment = { horizontal: 'center', vertical: 'middle' };
          installedCell.alignment = { horizontal: 'center', vertical: 'middle' };

          if (cell.expectedQty === 0) {
            installedCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isStriped ? stripe : 'FFFFFFFF' } };
          } else if (cell.actualQty >= cell.expectedQty) {
            installedCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } };
          } else if (cell.actualQty === 0) {
            installedCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE4E6' } };
          } else {
            installedCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF7CC' } };
          }

          const shortfallLines = row.shortfalls[format.formatKey]
            ?.map((shortfall) => `${shortfall.storeName}: Due ${shortfall.expectedQty}, Posted ${shortfall.actualQty}`)
            ?? [];
          if (shortfallLines.length > 0) {
            installedCell.note = shortfallLines.join('\n');
          }

          valueCol += 2;
        }

        worksheet.getCell(rowIndex, valueCol).value = row.totalStoreCount;
        worksheet.getCell(rowIndex, valueCol + 1).value = row.storesWithSomeInstalled;
        worksheet.getCell(rowIndex, valueCol + 2).value = row.storesWithAllInstalled;
        worksheet.getCell(rowIndex, valueCol + 3).value = '';
        for (let c = valueCol; c <= valueCol + 2; c += 1) {
          worksheet.getCell(rowIndex, c).alignment = { horizontal: 'center', vertical: 'middle' };
        }

        if (isStriped) {
          for (let c = 1; c <= totalColumns; c += 1) {
            const rowCell = worksheet.getCell(rowIndex, c);
            if (!rowCell.fill) {
              rowCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: stripe } };
            }
          }
        }

        rowIndex += 1;
      }

      worksheet.columns = [
        { width: 32 },
        ...overview.formats.flatMap(() => [{ width: 10 }, { width: 11 }]),
        { width: 16 },
        { width: 16 },
        { width: 16 },
        { width: 24 },
      ];

      for (let r = 1; r < rowIndex; r += 1) {
        for (let c = 1; c <= totalColumns; c += 1) {
          const cell = worksheet.getCell(r, c);
          cell.border = {
            top: { style: 'thin', color: { argb: borderColor } },
            left: { style: 'thin', color: { argb: borderColor } },
            bottom: { style: 'thin', color: { argb: borderColor } },
            right: { style: 'thin', color: { argb: borderColor } },
          };
        }
      }

      worksheet.autoFilter = {
        from: { row: pairHeaderRow, column: 1 },
        to: { row: pairHeaderRow, column: totalColumns },
      };

      const buffer = await workbook.xlsx.writeBuffer();
      const JSZip = (await import('jszip')).default;
      const zip = await JSZip.loadAsync(buffer);
      const vmlFiles = Object.keys(zip.files).filter((fileName) => /xl\/drawings\/vmlDrawing\d+\.vml$/.test(fileName));
      await Promise.all(vmlFiles.map(async (fileName) => {
        const file = zip.file(fileName);
        if (!file) return;
        const xml = await file.async('string');
        const enlargedXml = xml
          .replace(/width:97\.8pt;height:59\.1pt/g, 'width:360pt;height:220pt')
          .replace(/<x:Anchor>([^<]+)<\/x:Anchor>/g, (match, value) => {
            const parts = String(value).split(',').map((part) => Number(part.trim()));
            if (parts.length !== 8 || parts.some((part) => Number.isNaN(part))) return match;
            const [leftCol, leftOffset, topRow, topOffset] = parts;
            return `<x:Anchor>${[
              leftCol,
              leftOffset,
              topRow,
              topOffset,
              leftCol + 6,
              40,
              topRow + 12,
              16,
            ].join(', ')}</x:Anchor>`;
          });
        zip.file(fileName, enlargedXml);
      }));
      const patchedBuffer = await zip.generateAsync({ type: 'arraybuffer' });
      const blob = new Blob([patchedBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      a.href = url;
      a.download = `compliance-overview-cycle-${data.cycleId}-${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (exportError) {
      console.error('Compliance overview export failed', exportError);
      const msg = exportError instanceof Error ? exportError.message : String(exportError);
      window.alert(`Failed to export Excel file:\n${msg}`);
    } finally {
      setIsExporting(false);
    }
  };

  const hasExportableData = !!data && overview.rows.length > 0;

  const header = (
    <CardHeader className="pb-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <CardTitle>Compliance Overview</CardTitle>
        </div>
        <Button size="sm" variant="outline" onClick={handleExportExcel} disabled={isExporting || !hasExportableData}>
          <Download className="h-4 w-4 mr-2" />
          {isExporting ? 'Exporting...' : 'Export Excel'}
        </Button>
      </div>
    </CardHeader>
  );

  if (isLoading) {
    return (
      <Card>
        {header}
        <CardContent>
          <div className="text-sm text-muted-foreground">Loading compliance overview...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        {header}
        <CardContent>
          <div className="text-sm text-red-600">{error.message || 'Failed to load compliance overview'}</div>
        </CardContent>
      </Card>
    );
  }

  if (!data || overview.rows.length === 0) {
    return (
      <Card>
        {header}
        <CardContent>
          <div className="text-sm text-muted-foreground">No compliance overview rows found for the selected filters.</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      {header}
      <CardContent>
        <div className="overflow-auto border rounded-md">
          <table className="w-full min-w-max border-collapse text-sm">
            <thead>
              <tr className="bg-slate-100">
                <th rowSpan={2} className="sticky left-0 z-20 min-w-[240px] border-b border-r px-3 py-2 text-left font-semibold bg-slate-100">
                  Campaign
                </th>
                {overview.formats.map((format) => (
                  <th
                    key={format.formatKey}
                    colSpan={2}
                    className="border-b border-r px-2 py-2 text-center font-semibold"
                  >
                    {format.formatName}
                  </th>
                ))}
                <th rowSpan={2} className="border-b border-r px-3 py-2 text-center font-semibold bg-slate-100 min-w-[140px]">
                  Total no. of stores in campaign
                </th>
                <th rowSpan={2} className="border-b border-r px-3 py-2 text-center font-semibold bg-slate-100 min-w-[150px]">
                  Stores with some POS installed
                </th>
                <th rowSpan={2} className="border-b border-r px-3 py-2 text-center font-semibold bg-slate-100 min-w-[150px]">
                  Stores with all POS installed
                </th>
                <th rowSpan={2} className="border-b px-3 py-2 text-left font-semibold bg-slate-100 min-w-[180px]">
                  Notes
                </th>
              </tr>
              <tr className="bg-white">
                {overview.formats.flatMap((format) => ([
                  <th key={`${format.formatKey}-expected`} className="border-b border-r px-2 py-1 text-center font-medium">
                    Due
                  </th>,
                  <th key={`${format.formatKey}-actual`} className="border-b border-r px-2 py-1 text-center font-medium">
                    Installed
                  </th>,
                ]))}
              </tr>
            </thead>
            <tbody>
              {overview.rows.map((row) => (
                <tr key={row.campaignId} className="odd:bg-white even:bg-slate-50/40">
                  <td className="sticky left-0 z-10 border-r border-b bg-inherit px-3 py-2 align-top font-medium">
                    {row.campaignName}
                  </td>
                  {overview.formats.flatMap((format) => {
                    const cell = row.cells[format.formatKey] ?? { expectedQty: 0, actualQty: 0 };
                    return [
                      <td
                        key={`${row.campaignId}-${format.formatKey}-expected`}
                        className="border-r border-b px-2 py-2 text-center tabular-nums"
                      >
                        {cell.expectedQty}
                      </td>,
                      <td
                        key={`${row.campaignId}-${format.formatKey}-actual`}
                        className={`border-r border-b px-2 py-2 text-center tabular-nums ${getActualCellClasses(cell.expectedQty, cell.actualQty)}`}
                      >
                        {cell.actualQty}
                      </td>,
                    ];
                  })}
                  <td className="border-r border-b px-3 py-2 text-center tabular-nums">{row.totalStoreCount}</td>
                  <td className="border-r border-b px-3 py-2 text-center tabular-nums">{row.storesWithSomeInstalled}</td>
                  <td className="border-r border-b px-3 py-2 text-center tabular-nums">{row.storesWithAllInstalled}</td>
                  <td className="border-b px-3 py-2" />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default ComplianceDetailedGrid;
